import { useState } from 'react'
import { Check, Loader2, Pencil, RotateCcw, X } from 'lucide-react'
import type { Breakdown } from '../lib/types'
import { ago, clock, duration } from '../lib/format'
import { useFleet } from '../state/store'
import {
  clearBreakdown, dismissResumedMoving, editBreakdownReportedAt, updateBreakdownNote,
} from '../lib/breakdownApi'
import { localInputValueToMs, msToLocalInputValue } from '../lib/localDateTime'

function whenMoved(ts: number | null | undefined): string {
  if (ts == null) return '—'
  return new Date(ts).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
  })
}

/**
 * The resolve control the breakdown module was missing: reachable in one click, without
 * opening the edit form first. Sits on the vehicle card, the drawer footer, the
 * actionables row and the Breakdowns panel -- every place a supervisor first sees a
 * broken-down truck.
 *
 * Two editable timestamps, two different reasons:
 *  - "reported at" (opened_ts) can be corrected if someone fat-fingered it when marking
 *    the truck down -- see editBreakdownReportedAt.
 *  - "resolved at" is asked for ONLY when resolving, pre-filled from resumed_moving_ts
 *    (telemetry's own first-movement evidence, speed > 2 kph) when there is one. When
 *    there is not -- OBD offline the whole time -- the operator has to supply one, because
 *    the alternative is silently defaulting to "now", which overstates every downtime by
 *    however long it took someone to notice the truck was back.
 */
export function BreakdownQuickResolve({
  vehicleId, breakdown, compact, onResolved,
}: {
  vehicleId: string; breakdown: Breakdown; compact?: boolean
  /** Optional: for surfaces with no SSE subscription of their own (a polled report panel),
   *  so the row disappears immediately instead of waiting for the next poll. */
  onResolved?: () => void
}): JSX.Element {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [editingReported, setEditingReported] = useState(false)
  const [reportedInput, setReportedInput] = useState('')
  const [resolving, setResolving] = useState(false)
  const [resolvedInput, setResolvedInput] = useState('')
  const [editingNote, setEditingNote] = useState(false)
  const [noteInput, setNoteInput] = useState('')
  const [noteOverride, setNoteOverride] = useState<string | null>(null)

  const reportedTs = breakdown.opened_ts ?? breakdown.ts
  // The prop is the source of truth once SSE or the next poll catches up; the override
  // just avoids the saved note flashing back to the stale value in between.
  const note = noteOverride ?? breakdown.note
  // Idling since it was reported down, until telemetry saw it move again (or now, if it
  // hasn't). Not the same number as a trip's idle_sec -- this is specific to the
  // breakdown, the one duration an operator actually wants off this card: how long has
  // it been sitting there.
  const idleSec = ((breakdown.resumed_moving_ts ?? Date.now()) - reportedTs) / 1000

  function startEditNote(): void {
    setNoteInput(note ?? '')
    setEditingNote(true)
  }

  async function saveNote(): Promise<void> {
    const trimmed = noteInput.trim()
    setBusy(true)
    setError(null)
    try {
      // Applied directly, same reason confirmResolve below does: the response body is
      // the same entry the server broadcasts over SSE, and on the 2-pod deployment (no
      // session affinity) that broadcast can land on a different pod than this request.
      useFleet.getState().applyEvent({ type: 'breakdown', data: await updateBreakdownNote(vehicleId, breakdown, trimmed) })
      setNoteOverride(trimmed)
      setEditingNote(false)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'could not update note')
    } finally {
      setBusy(false)
    }
  }

  function startEditReported(): void {
    setReportedInput(msToLocalInputValue(reportedTs))
    setEditingReported(true)
  }

  async function saveReported(): Promise<void> {
    const ms = localInputValueToMs(reportedInput)
    if (ms == null) { setEditingReported(false); return }
    setBusy(true)
    setError(null)
    try {
      await editBreakdownReportedAt(vehicleId, ms)
      setEditingReported(false)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'could not update reported time')
    } finally {
      setBusy(false)
    }
  }

  function startResolve(): void {
    setResolvedInput(msToLocalInputValue(breakdown.resumed_moving_ts ?? Date.now()))
    setResolving(true)
  }

  async function dismiss(): Promise<void> {
    setBusy(true)
    setError(null)
    try {
      // Applied directly, same reason confirmResolve below does: cross-pod SSE delivery
      // gap, this browser's connection may be pinned to a different pod than the one
      // that handled this request.
      useFleet.getState().applyEvent({ type: 'breakdown', data: await dismissResumedMoving(vehicleId) })
    } catch (e) {
      setError(e instanceof Error ? e.message : 'could not dismiss')
    } finally {
      setBusy(false)
    }
  }

  async function confirmResolve(): Promise<void> {
    const ms = localInputValueToMs(resolvedInput)
    setBusy(true)
    setError(null)
    try {
      // Applied directly instead of waiting on the SSE rebroadcast -- see BreakdownForm's
      // submit() for the full reasoning (cross-pod SSE delivery gap, confirmed root cause
      // of "resolved on screen but still shows down until a hard refresh").
      const result = await clearBreakdown(vehicleId, ms ?? undefined)
      useFleet.getState().applyEvent({ type: 'breakdown', data: result })
      setResolving(false)
      onResolved?.()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'could not resolve')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="flex flex-col gap-1">
      <div className={`flex items-center gap-1.5 ${compact ? 'text-2xs' : 'text-[11.5px]'} text-muted`}>
        {editingReported ? (
          <>
            <input
              type="datetime-local" value={reportedInput}
              onChange={(e) => setReportedInput(e.target.value)}
              className="rounded border border-line bg-white px-1 py-0.5 font-mono text-2xs text-ink"
            />
            <button type="button" onClick={() => void saveReported()} disabled={busy}
                    className="text-emerald-600 hover:text-emerald-700" aria-label="save reported time">
              <Check size={12} />
            </button>
            <button type="button" onClick={() => setEditingReported(false)}
                    className="text-muted hover:text-ink" aria-label="cancel">
              <X size={12} />
            </button>
          </>
        ) : (
          <>
            <span>
              reported {ago(reportedTs)} ago · {clock(reportedTs)}
              {breakdown.by ? <span className="text-slate-400"> · by {breakdown.by}</span> : null}
            </span>
            <button type="button" onClick={startEditReported}
                    className="text-muted hover:text-ink" aria-label="edit reported time" title="Edit reported time">
              <Pencil size={11} />
            </button>
          </>
        )}
      </div>

      <div className={`${compact ? 'text-2xs' : 'text-[11.5px]'} font-medium ${
        breakdown.resumed_moving_ts != null ? 'text-slate-400 line-through' : 'text-amber-700'
      }`}>
        idling {duration(idleSec)}
      </div>

      <div className={`flex items-start gap-1.5 ${compact ? 'text-2xs' : 'text-[11.5px]'}`}>
        {editingNote ? (
          <>
            <textarea
              value={noteInput} rows={2} autoFocus
              onChange={(e) => setNoteInput(e.target.value)}
              placeholder="where it stopped, what was tried, who is attending"
              className="w-full resize-none rounded border border-line bg-white px-1.5 py-1 text-ink"
            />
            <button type="button" onClick={() => void saveNote()} disabled={busy}
                    className="mt-1 shrink-0 text-emerald-600 hover:text-emerald-700" aria-label="save note">
              <Check size={12} />
            </button>
            <button type="button" onClick={() => setEditingNote(false)}
                    className="mt-1 shrink-0 text-muted hover:text-ink" aria-label="cancel">
              <X size={12} />
            </button>
          </>
        ) : (
          <>
            <span className="text-muted">{note || 'no note'}</span>
            <button type="button" onClick={startEditNote}
                    className="shrink-0 text-muted hover:text-ink" aria-label="edit note" title="Edit note">
              <Pencil size={11} />
            </button>
          </>
        )}
      </div>

      {breakdown.resumed_moving_ts != null && !resolving && (
        <div className="flex items-center justify-between gap-2 rounded-md border border-amber-300/60
                        bg-amber-50 px-2 py-1.5 text-2xs text-amber-800">
          <span>started moving at {whenMoved(breakdown.resumed_moving_ts)} — resolve?</span>
          <div className="flex shrink-0 items-center gap-1">
            <button
              type="button" onClick={() => void dismiss()} disabled={busy}
              className="rounded border border-amber-400/40 bg-white px-1.5 py-0.5 text-amber-700
                         hover:bg-amber-100 disabled:opacity-50"
              title="Not it -- still stuck, telemetry can flag this again"
            >
              Dismiss
            </button>
            <button
              type="button" onClick={startResolve} disabled={busy}
              className="flex items-center gap-1 rounded border border-amber-400/70 bg-white
                         px-1.5 py-0.5 font-medium text-amber-800 hover:bg-amber-100 disabled:opacity-50"
            >
              <RotateCcw size={11} />
              Resolve
            </button>
          </div>
        </div>
      )}

      {breakdown.resumed_moving_ts == null && !resolving && (
        <button
          type="button" onClick={startResolve} disabled={busy}
          className="flex w-fit items-center gap-1.5 rounded-lg border border-line px-2.5 py-1.5
                     text-[11.5px] text-[#047857] transition-colors hover:bg-[rgba(16,185,129,0.08)]
                     disabled:opacity-50"
        >
          <RotateCcw size={12} />
          Back in service
        </button>
      )}

      {resolving && (
        <div className="grid gap-1.5 rounded-md border border-line bg-high/60 p-2">
          <label className="flex flex-col gap-1 text-2xs text-muted">
            resolved at
            {breakdown.resumed_moving_ts == null && (
              <span className="text-[10px] text-amber-700">
                no telemetry saw this truck moving — enter when it was actually fixed
              </span>
            )}
            <input
              type="datetime-local" value={resolvedInput}
              onChange={(e) => setResolvedInput(e.target.value)}
              className="rounded border border-line bg-white px-1.5 py-1 font-mono text-2xs text-ink"
            />
          </label>
          <div className="flex items-center gap-1.5">
            <button type="button" onClick={() => void confirmResolve()} disabled={busy}
                    className="flex items-center gap-1 rounded-lg bg-[#047857] px-2.5 py-1 text-2xs
                               font-medium text-white hover:opacity-90 disabled:opacity-50">
              {busy ? <Loader2 size={11} className="animate-spin" /> : <Check size={11} />}
              Confirm resolved
            </button>
            <button type="button" onClick={() => setResolving(false)} disabled={busy}
                    className="rounded-lg px-2.5 py-1 text-2xs text-muted hover:bg-high">
              Cancel
            </button>
          </div>
        </div>
      )}

      {error && <p className="text-2xs text-danger">Not saved — {error}</p>}
    </div>
  )
}
